import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { Users, Mail, Trash2, Copy, Check, Plus, AlertCircle, Crown, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import ConfirmModal from "../components/ConfirmModal";

export default function Team() {
  const { active, refresh: refreshWorkspaces } = useWorkspace();
  const wsId = active?.id;
  const isAdmin = active?.role === "admin";

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [copiedToken, setCopiedToken] = useState(null);
  const [confirm, setConfirm] = useState(null);  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [mr, ir] = await Promise.all([
        api.get(`/workspaces/${wsId}/members`),
        isAdmin ? api.get(`/workspaces/${wsId}/invites`) : Promise.resolve({ data: [] }),
      ]);
      setMembers(mr.data || []);
      setInvites(ir.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [wsId, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const submitInvite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setInviting(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/invites`, { email, role: inviteRole });
      if (data.reused) {
        toast.info(`Invite for ${email} already exists — link copied`);
      } else {
        toast.success(`Invite created for ${email}`);
      }
      try { await navigator.clipboard.writeText(data.invite_url); } catch { /* ignore */ }
      setCopiedToken(data.token);
      setTimeout(() => setCopiedToken(null), 2000);
      setInviteEmail("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.invite_url);
      setCopiedToken(inv.token);
      toast.success("Invite link copied");
      setTimeout(() => setCopiedToken(null), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const revokeInvite = (inv) => {
    setConfirm({
      title: "Revoke invite",
      body: `Revoke invite for ${inv.email}? They won't be able to join with the existing link.`,
      destructive: true,
      confirmLabel: "Revoke",
      onConfirm: async () => {
        try {
          await api.delete(`/workspaces/${wsId}/invites/${inv.id}`);
          toast.success("Invite revoked");
          await load();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  const removeMember = (m) => {
    if (m.is_you) {
      toast.error("You can't remove yourself");
      return;
    }
    setConfirm({
      title: "Remove member",
      body: `Remove ${m.name} (${m.email}) from "${active?.name}"? They lose access to all leads, campaigns, and activities in this workspace.`,
      destructive: true,
      confirmLabel: "Remove",
      onConfirm: async () => {
        try {
          await api.delete(`/workspaces/${wsId}/members/${m.user_id}`);
          toast.success(`${m.name} removed`);
          await load();
          await refreshWorkspaces();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      },
    });
  };

  if (!wsId) {
    return (
      <div className="p-8">
        <div className="flex items-center gap-3 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading workspace…
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl" data-testid="team-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
          <Users className="w-3.5 h-3.5" />
          Workspace · {active?.name}
        </div>
        <h1 className="font-heading font-black text-4xl tracking-tight">Team</h1>
        <p className="text-zinc-600 mt-2 text-sm">
          Invite teammates to collaborate inside this workspace. Members see the same leads, campaigns, and activities.
          Data is isolated from other workspaces.
        </p>
      </div>

      {!isAdmin && (
        <div className="mb-6 flex items-start gap-2 border-l-2 border-zinc-300 bg-zinc-50 p-4 text-sm">
          <AlertCircle className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Member view</div>
            <div className="text-zinc-600 mt-0.5">Only workspace admins can invite or remove teammates.</div>
          </div>
        </div>
      )}

      {/* Invite form (admin only) */}
      {isAdmin && (
        <div className="mb-10 border border-zinc-200 p-5" data-testid="invite-form-card">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-[#002FA7]" strokeWidth={2.5} />
            <h2 className="font-heading font-bold text-base uppercase tracking-wider">Invite a teammate</h2>
          </div>
          <form onSubmit={submitInvite} className="flex flex-col sm:flex-row gap-2 items-stretch">
            <input
              data-testid="invite-email-input"
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@company.com"
              className="flex-1 px-3 py-2 border border-zinc-300 focus:outline-none focus:border-[#002FA7] text-sm"
            />
            <select
              data-testid="invite-role-select"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="px-3 py-2 border border-zinc-300 focus:outline-none focus:border-[#002FA7] text-sm"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              data-testid="invite-submit-btn"
              type="submit"
              disabled={inviting}
              className="px-5 py-2 bg-[#002FA7] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#001F73] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
              {inviting ? "Sending…" : "Create invite"}
            </button>
          </form>
          <p className="text-xs text-zinc-500 mt-2">
            Share the generated link with your teammate. Invites are single-use and expire in 14 days.
          </p>
        </div>
      )}

      {/* Pending invites */}
      {isAdmin && invites.length > 0 && (
        <div className="mb-10">
          <h2 className="font-heading font-bold text-sm uppercase tracking-wider mb-3 text-zinc-700">
            Pending invites <span className="text-zinc-400 font-mono">({invites.length})</span>
          </h2>
          <div className="border border-zinc-200" data-testid="pending-invites-list">
            {invites.map((inv) => (
              <div
                key={inv.id}
                data-testid={`invite-row-${inv.token}`}
                className="flex items-center gap-3 px-4 py-3 border-b border-zinc-200 last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{inv.email}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                    <span className="uppercase tracking-wider">{inv.role}</span>
                    <span>·</span>
                    <span>expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <button
                  data-testid={`invite-copy-${inv.token}`}
                  onClick={() => copyInviteLink(inv)}
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-zinc-300 hover:bg-zinc-100 flex items-center gap-1.5"
                  title="Copy invite link"
                >
                  {copiedToken === inv.token ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedToken === inv.token ? "Copied" : "Copy link"}
                </button>
                <button
                  data-testid={`invite-revoke-${inv.token}`}
                  onClick={() => revokeInvite(inv)}
                  className="px-2 py-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50"
                  title="Revoke invite"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div>
        <h2 className="font-heading font-bold text-sm uppercase tracking-wider mb-3 text-zinc-700">
          Members <span className="text-zinc-400 font-mono">({members.length})</span>
        </h2>
        {loading ? (
          <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="border border-zinc-200" data-testid="members-list">
            {members.map((m) => (
              <div
                key={m.user_id}
                data-testid={`member-row-${m.user_id}`}
                className="flex items-center gap-3 px-4 py-3 border-b border-zinc-200 last:border-b-0"
              >
                <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">
                  {m.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold truncate">{m.name}</span>
                    {m.is_you && <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1.5 py-0.5">You</span>}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 bg-zinc-50 border border-zinc-200">
                  {m.role === "admin" && <Crown className="w-3 h-3 text-amber-600" />}
                  <span className="text-[11px] font-semibold uppercase tracking-wider">{m.role}</span>
                </div>
                {isAdmin && !m.is_you && (
                  <button
                    data-testid={`member-remove-${m.user_id}`}
                    onClick={() => removeMember(m)}
                    className="p-2 text-zinc-500 hover:text-red-600 hover:bg-red-50"
                    title="Remove member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {confirm && (
        <ConfirmModal
          modal={confirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
